import { Helmet } from 'react-helmet-async'
import { SITE_ORIGIN } from '../../seo/routes'

interface SeoProps {
  title: string
  description: string
  /** Route path, e.g. "/about" — canonical is built as SITE_ORIGIN + path. */
  path: string
  ogImage?: string
  type?: 'website' | 'article'
  /** JSON-LD objects rendered as application/ld+json script blocks. */
  jsonLd?: object[]
  noindex?: boolean
  /** LCP hero image to preload (path, e.g. /media/town-lewes.jpg). */
  preloadImage?: string
  /** Width of the full-size preloadImage (default 1280; home hero is 1920). */
  preloadImageWidth?: number
}

const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/media/merritt-shawn-og.jpg`

export default function Seo({
  title,
  description,
  path,
  ogImage = DEFAULT_OG_IMAGE,
  type = 'website',
  jsonLd = [],
  noindex = false,
  preloadImage,
  preloadImageWidth = 1280,
}: SeoProps) {
  const canonical = `${SITE_ORIGIN}${path}`
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {preloadImage && (
        <link
          rel="preload"
          as="image"
          href={preloadImage}
          imageSrcSet={`${preloadImage.replace('.jpg', '-768.jpg')} 768w, ${preloadImage.replace('.jpg', '-1024.jpg')} 1024w, ${preloadImage} ${preloadImageWidth}w`}
          imageSizes="100vw"
          fetchPriority="high"
        />
      )}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )
}
